import React, { useMemo } from 'react'
import { SUBJECT_TYPES } from '@aytm/app/insights/dashboard/lib/config'
import usePool from '@duckness/use-pool'
import InsightsDashboardPool from '@aytm/app/insights/dashboard/duckness/InsightsDashboardPool'

import {
  Editor,
  Subject,
  TrackTargetSelector,
  TrackAttributesSelector
} from '@aytm/app/insights/dashboard/components/ui'

import {
  useValue,
  useRow,
  useDashboard,
  useAttributes,
  useDateTime
} from '@aytm/app/insights/dashboard/components/hooks'

// const halfEmHeightStyle = { height: '0.5em', flex: '0 0 0.5em' }

/**
 *
 * @param {*} param0
 * @returns
 */
export function useSubjects({
  subjectType,
  dragItemIndex,
  editor: propsEditor,
  items: propsItems,
  onBenchmarkDelete,
  onEditorChange,
  onImageUpload,
  onItemAdd,
  onItemDelete,
  onItemDrag,
  onItemHover,
  onItemPin,
  onItemSelect,
  onItemReplace,
  onSwapChange,
  onTrackAttributesChange,
  onTrackTargetChange,
  wait
}) {
  const isConcept = SUBJECT_TYPES.CONCEPT === subjectType

  // Block info
  const { blockModel } = useDashboard()

  // Component store properties

  const { subjects, data, splitOptions } = usePool(
    InsightsDashboardPool,
    state => {
      const store = state.currentData
      return { subjects: store?.subjects || null, data: store?.data || null, splitOptions: store?.splitOptions || [] }
    },
    (prev, next) =>
      !prev && !next
        ? false
        : (!prev && Boolean(next)) ||
          (prev && !next) ||
          prev.subjects !== next.subjects ||
          prev.data !== next.data ||
          prev.splitOptions !== next.splitOptions
  )

  // Number of split options
  const splitOptionsCount = splitOptions?.length || 1

  /** WAVES: List of all attributes contained in last fetched schema */
  const { attributes } = useAttributes()

  // Block model properties

  /** Sort by attribute */
  const sortByAttribute = useMemo(() => blockModel?.getObject('sortByAttribute') || null, [blockModel])

  /** Show/Hide ranks */
  const showRanks = useMemo(() => blockModel?.get('showRanks'), [blockModel])

  /** Show/Hide images */
  const showImages = useMemo(() => (isConcept ? blockModel?.get('showImages') : false), [isConcept, blockModel])

  /** Data source (attribute or entity) */
  const trackTarget = useMemo(() => blockModel?.getObject('trackTarget') || 'entity', [blockModel])

  /** Object contains tracking attributes names like { attribute: <...>, entity: <...> } */
  const trackAttributes = useMemo(() => blockModel?.getJS('trackAttributes') || {}, [blockModel])

  /** Current split option */
  const currentSplit = useMemo(() => blockModel?.getObject('currentSplit') || null, [blockModel])

  /** Set for current items */
  const items = useMemo(() => propsItems || [], [propsItems])

  /** Current editor state */
  const editor = useMemo(() => propsEditor || null, [propsEditor])

  const isSplitOn = useMemo(() => Boolean(blockModel?.getObject('currentSplit') || null), [blockModel])
  const isSwapOn = useMemo(() => blockModel?.getObject('swap') || false, [blockModel])

  const showImagesNotApplicable = useMemo(() => isSplitOn && isSwapOn, [isSplitOn, isSwapOn])

  /** getColumnData function */
  const { getColumnData } = useValue()

  /** renderDateRange function */
  const { renderDateRange } = useDateTime()

  /** getRow function */
  const { getRow } = useRow({ items, editor })

  return useMemo(
    () => [
      getRow(
        // 1: Produce first cell
        ({ key }) =>
          isConcept ? (
            <Subject.Empty key={key}></Subject.Empty>
          ) : (
            <Subject.Empty key={key}>
              <TrackTargetSelector trackTarget={trackTarget} onTrackTargetChange={onTrackTargetChange} />
              <TrackAttributesSelector
                trackTarget={trackTarget}
                trackAttributes={trackAttributes}
                attributes={attributes}
                onTrackAttributesChange={onTrackAttributesChange}
              />
            </Subject.Empty>
          ),
        // 2: Produce in place subject title editors
        ({ key }) => (
          <Editor
            key={key}
            rowType={'subject'}
            editor={editor}
            subjects={subjects}
            showView={!showImagesNotApplicable && showImages}
            subjectType={subjectType}
            wait={wait}
            onItemAdd={onItemAdd}
            onEditorChange={onEditorChange}
            onImageUpload={onImageUpload}
          />
        ),
        // 3: Produce subject common cells
        ({ key, itemIndex, item, uid, isExtraColumn, isEditing }) => {
          const subject = subjects?.[uid] || null
          return isExtraColumn ? (
            <div key={key}></div>
          ) : subject && 0 === itemIndex % splitOptionsCount ? (
            <Subject
              key={key}
              item={item}
              items={items}
              index={itemIndex}
              subject={subject}
              subjects={subjects}
              data={data}
              sortByAttribute={sortByAttribute}
              showRank={showRanks}
              showImage={!showImagesNotApplicable && showImages}
              showDelete={!isEditing}
              editor={editor}
              wait={wait}
              dragItemIndex={dragItemIndex}
              blink={!currentSplit}
              swap={currentSplit}
              onPin={onItemPin}
              onSelect={onItemSelect}
              onReplace={onItemReplace}
              onDelete={onItemDelete}
              onRemove={onBenchmarkDelete}
              onDrag={onItemDrag}
              onHover={onItemHover}
              onGetColumnData={getColumnData}
              onRenderDateRange={renderDateRange}
              onSwapChange={onSwapChange}
              onEditorChange={onEditorChange}
            />
          ) : null
        }
      )
    ],
    [
      attributes,
      currentSplit,
      data,
      dragItemIndex,
      editor,
      getColumnData,
      getRow,
      isConcept,
      items,
      onBenchmarkDelete,
      onEditorChange,
      onImageUpload,
      onItemAdd,
      onItemDelete,
      onItemDrag,
      onItemHover,
      onItemPin,
      onItemReplace,
      onItemSelect,
      onSwapChange,
      onTrackAttributesChange,
      onTrackTargetChange,
      renderDateRange,
      showImages,
      showImagesNotApplicable,
      showRanks,
      sortByAttribute,
      splitOptionsCount,
      subjectType,
      subjects,
      trackAttributes,
      trackTarget,
      wait
    ]
  )
}
